import apiClient from './api';

// Interface pour les logs d'audit
export interface AuditLog {
  id: string;
  actorId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'ARCHIVE' | 'RESTORE' | 'MOVE' | 'EXPORT' | 'IMPORT';
  resourceType: 'SITE' | 'BUILDING' | 'PIECE' | 'ZONE' | 'BOX' | 'ITEM' | 'USER';
  resourceId: string;
  previousValue?: any;
  newValue?: any;
  source: 'UI' | 'MOBILE' | 'API' | 'IMPORT';
  timestamp: Date;
  scope: string;
  ipAddress?: string;
  userAgent?: string;
}

// Interface pour créer un log d'audit
export interface CreateAuditLogData {
  action: AuditLog['action'];
  resourceType: AuditLog['resourceType'];
  resourceId: string;
  previousValue?: any;
  newValue?: any;
  source?: AuditLog['source'];
  scope?: string;
}

export const auditService = {
  // Créer un log d'audit
  async createLog(data: CreateAuditLogData): Promise<AuditLog> {
    try {
      const response = await apiClient.post('/audit/logs', {
        ...data,
        source: data.source || 'UI',
        timestamp: new Date().toISOString(),
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du log d\'audit:', error);
      throw error;
    }
  },

  // Récupérer les logs d'audit
  async getLogs(filters?: {
    actorId?: string;
    action?: string;
    resourceType?: string;
    resourceId?: string;
    startDate?: string;
    endDate?: string;
    scope?: string;
  }): Promise<AuditLog[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.actorId) params.append('actorId', filters.actorId);
      if (filters?.action) params.append('action', filters.action);
      if (filters?.resourceType) params.append('resourceType', filters.resourceType);
      if (filters?.resourceId) params.append('resourceId', filters.resourceId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.scope) params.append('scope', filters.scope);

      const response = await apiClient.get(`/audit/logs?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des logs d\'audit:', error);
      throw error;
    }
  },

  // Récupérer l'historique d'une ressource
  async getResourceHistory(resourceType: string, resourceId: string): Promise<AuditLog[]> {
    try {
      const response = await apiClient.get(`/audit/logs/resource/${resourceType}/${resourceId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      throw error;
    }
  },

  // Récupérer l'IP du client (simulation)
  async getClientIP(): Promise<string> {
    try {
      // En production, utiliser un service d'IP ou récupérer depuis les headers
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn('Impossible de récupérer l\'IP du client:', error);
      return 'unknown';
    }
  },

  // Logger une action de création
  async logCreate(resourceType: AuditLog['resourceType'], resourceId: string, newValue: any, scope?: string): Promise<void> {
    await this.createLog({
      action: 'CREATE',
      resourceType,
      resourceId,
      newValue,
      scope
    });
  },

  // Logger une action de modification
  async logUpdate(resourceType: AuditLog['resourceType'], resourceId: string, previousValue: any, newValue: any, scope?: string): Promise<void> {
    await this.createLog({
      action: 'UPDATE',
      resourceType,
      resourceId,
      previousValue,
      newValue,
      scope
    });
  },

  // Logger une action de suppression
  async logDelete(resourceType: AuditLog['resourceType'], resourceId: string, previousValue: any, scope?: string): Promise<void> {
    await this.createLog({
      action: 'DELETE',
      resourceType,
      resourceId,
      previousValue,
      scope
    });
  },

  // Logger une action d'archivage
  async logArchive(resourceType: AuditLog['resourceType'], resourceId: string, previousValue: any, scope?: string): Promise<void> {
    await this.createLog({
      action: 'ARCHIVE',
      resourceType,
      resourceId,
      previousValue,
      scope
    });
  },

  // Logger une action de restauration
  async logRestore(resourceType: AuditLog['resourceType'], resourceId: string, newValue: any, scope?: string): Promise<void> {
    await this.createLog({
      action: 'RESTORE',
      resourceType,
      resourceId,
      newValue,
      scope
    });
  },

  // Logger une action de déplacement
  async logMove(resourceType: AuditLog['resourceType'], resourceId: string, previousValue: any, newValue: any, scope?: string): Promise<void> {
    await this.createLog({
      action: 'MOVE',
      resourceType,
      resourceId,
      previousValue,
      newValue,
      scope
    });
  },

  // Logger une action d'export
  async logExport(resourceType: AuditLog['resourceType'], resourceId: string, scope?: string): Promise<void> {
    await this.createLog({
      action: 'EXPORT',
      resourceType,
      resourceId,
      scope
    });
  },

  // Logger une action d'import
  async logImport(resourceType: AuditLog['resourceType'], resourceId: string, newValue: any, scope?: string): Promise<void> {
    await this.createLog({
      action: 'IMPORT',
      resourceType,
      resourceId,
      newValue,
      scope
    });
  }
};

export default auditService;
